import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MoonshotStep1.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ---------- NEW QUESTIONNAIRE CONFIGURATION ---------- */
const steps = [
  {
    id: "role",
    type: "select",
    label: "I am a:",
    options: ["Founder"],
    required: true,
    helperText: ""
  },
  {
    id: "outcome_group", // ID changed to denote composite group
    type: "composite",
    label: "What outcome are you trying to create?",
    helperText: "",
    fields: [
      {
        id: "outcome",
        label: "Outcome",
        type: "textarea", // New type support in composite
        placeholder: "e.g. Build a fintech app for rural India within 12 months...",
        maxLength: 200,
        required: true
      },
      {
        id: "timeHorizon",
        label: "Time Horizon",
        type: "select",
        options: ["6 months", "12 months", "18 months", "24 months", "36 months", "5+ years", "Custom"],
        required: true
      }
    ]
  },
  {
    id: "unresolved",
    type: "multiselect",
    label: "What feels most unresolved right now?",
    options: [
      "Market clarity",
      "User clarity",
      "Distribution",
      "Business model",
      "Technology",
      "Regulation",
      "Capital",
      "Team"
    ],
    maxSelection: 2,
    required: true,
    helperText: "Select up to 2 areas."
  },
  {
    id: "target_user",
    type: "composite",
    label: "Target User (Compressed ICP)",
    fields: [
      {
        id: "userType",
        label: "User type",
        type: "select",
        options: ["Consumer", "SMB", "Enterprise", "Developer", "Other"],
        required: true
      },
      {
        id: "geography",
        label: "Geography",
        type: "text",
        placeholder: "e.g. India (Tier 2/3 Cities)",
        required: true
      },
      {
        id: "clarification",
        label: "Optional clarification",
        type: "textarea",
        placeholder: "Elaborate if needed",
        maxLength: 120
      }
    ]
  },
  {
    id: "belief",
    type: "bullets",
    label: "What do you believe to be true that others might not?",
    maxBullets: 2,
    maxLength: 120,
    placeholder: "e.g. Rural users trust local agents more than apps",
    required: true,
    helperText: ""
  },
  {
    id: "facts",
    type: "composite",
    label: "Key Facts",
    fields: [
      {
        id: "capital",
        label: "Capital",
        type: "select",
        options: ["Bootstrapped", "Angel", "VC"],
        required: true
      },
      {
        id: "team",
        label: "Team",
        type: "select",
        options: ["Solo", "2–5", "6–15"],
        required: true
      },
      {
        id: "time",
        label: "Time commitment",
        type: "select",
        options: ["Full-time", "Part-time"],
        required: true
      }
    ]
  },
  {
    id: "scope",
    type: "composite",
    label: "Scope Lock",
    fields: [
      {
        id: "region",
        label: "Primary region",
        type: "text",
        required: true,
        placeholder: "e.g. Southeast Asia"
      }
    ]
  }
];

export default function MoonshotStep1() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Form State: 
  const [form, setForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const s = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;

  /* ------------- FIELD HANDLERS ------------- */

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const updateComposite = (parentKey, fieldKey, value) => {
    setForm(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [fieldKey]: value
      }
    }));
  };

  const toggleMultiSelect = (key, option, max) => {
    const current = form[key] || [];
    if (current.includes(option)) {
      updateForm(key, current.filter(item => item !== option));
    } else {
      if (current.length < max) {
        updateForm(key, [...current, option]);
      }
    }
  };

  const updateBullet = (key, index, value) => {
    const current = form[key] || ["", ""];
    const newBullets = [...current];
    newBullets[index] = value;
    updateForm(key, newBullets);
  };

  /* ------------- VALIDATION ------------- */
  const isValid = () => {
    if (s.type === 'composite') {
      return s.fields.every(f => {
        if (!f.required) return true;
        const val = form[s.id]?.[f.id];

        // Special check for outcome_group timeHorizon Custom
        if (s.id === 'outcome_group' && f.id === 'timeHorizon' && val === 'Custom') {
          const customVal = form[s.id]?.['timeHorizon_custom'];
          return customVal && customVal.trim().length > 0;
        }

        return val && val.trim().length > 0;
      });
    }
    if (s.type === 'multiselect') {
      return (form[s.id] || []).length > 0;
    }
    if (s.type === 'bullets') {
      const bullets = form[s.id] || [];
      // Require at least first bullet if step is required, but ideally both if strict.
      // User said "add to bullet points", usually implies filling them out. 
      // We'll require at least one for now to be safe, or check all if needed?
      // Let's require at least one non-empty string.
      return bullets.some(b => b.trim().length > 0);
    }
    // Simple fields (text/select/textarea)
    if (s.required && (!form[s.id] || form[s.id].trim().length === 0)) return false;
    return true;
  };

  /* ------------- NAV HANDLERS ------------- */
  const nextStep = () => {
    if (isValid()) setStep(step + 1);
  };

  const prevStep = () => {
    if (step === 0) {
      navigate("/mainPage"); // Back to Dashboard
    } else {
      setStep(step - 1);
    }
  };

  /* ------------- SUBMIT ------------- */
  const createMinishot = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // 1. Construct Title & Description
      // Step 2 is now composite 'outcome_group' -> {outcome, timeHorizon}
      const outcomeData = form.outcome_group || {};
      const outcomeText = outcomeData.outcome || "";
      let outcomeTime = outcomeData.timeHorizon || "";

      // Handle custom timeline
      if (outcomeTime === "Custom" && outcomeData.timeHorizon_custom) {
        outcomeTime = outcomeData.timeHorizon_custom;
      }

      const title = outcomeText.trim().substring(0, 100) || "New Mission";

      const sections = [
        `Outcome: ${ outcomeText } (Technically in ${ outcomeTime })`,
        `Unresolved: ${ (form.unresolved || []).join(", ") }`,
        `Target User: ${ form.target_user?.userType } in ${ form.target_user?.geography }. ${ form.target_user?.clarification || '' }`,
        `Beliefs: ${ (form.belief || []).filter(b => b).join("; ") }`,
        `Facts: Capital=${ form.facts?.capital }, Team=${ form.facts?.team }, Time=${ form.facts?.time }`,
        `Scope: ${ form.scope?.region }`
      ];

      const description = sections.join("\n\n");
      const brief = outcomeText.substring(0, 497) + '...';

      // 2. Build Initial Questions Array for Backend
      const initialQuestions = steps.flatMap(stepItem => {
        if (stepItem.type === 'composite') {
          return stepItem.fields.map(f => {
            let answer = form[stepItem.id]?.[f.id] || "N/A";
            if (stepItem.id === 'outcome_group' && f.id === 'timeHorizon' && answer === 'Custom') {
              answer = form[stepItem.id]?.['timeHorizon_custom'] || "Custom";
            }
            return {
              questionId: `${ stepItem.id }_${ f.id }`,
              question: f.label,
              answer: answer
            };
          });
        }
        if (stepItem.type === 'multiselect') {
          return [{
            questionId: stepItem.id,
            question: stepItem.label,
            answer: (form[stepItem.id] || []).join(", ")
          }];
        }
        if (stepItem.type === 'bullets') {
          return [{
            questionId: stepItem.id,
            question: stepItem.label,
            answer: (form[stepItem.id] || []).filter(b => b).join("; ")
          }];
        }
        
        
        return [{
          questionId: stepItem.id,
          question: stepItem.label,
          answer: form[stepItem.id] || ""
        }];
      });

      const body = { title, description, brief, initialQuestions };

      console.log('🚀 Creating MiniShot:', body);

      const res = await fetch(`${ API_BASE_URL }/api/minishots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create");
      }

      const created = await res.json();
      navigate("/mainPage", { state: { openMiniShot: created, justCreated: true } });

    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ------------- DYNAMIC RENDERER ------------- */
  const renderInput = () => {
    switch (s.type) {
      case "select":
        return (
          <select
            className="custom-select"
            value={form[s.id] || ""}
            onChange={(e) => updateForm(s.id, e.target.value)}
          >
            <option value="" disabled>Select an option</option>
            {s.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case "textarea":
        return (
          <div className="input-group">
            <textarea
              className="idea-input"
              placeholder={s.placeholder}
              maxLength={s.maxLength}
              value={form[s.id] || ""}
              onChange={(e) => updateForm(s.id, e.target.value)}
            />
            <div className="character-count">
              {(form[s.id] || "").length}/{s.maxLength} chars
            </div>
          </div>
        );

      case "multiselect":
        const selected = form[s.id] || [];
        return (
          <div className="multi-select-grid">
            {s.options.map(opt => {
              const isActive = selected.includes(opt);
              return (
                <button
                  key={opt}
                  className={`toggle-btn ${ isActive ? "active" : "" }`}
                  onClick={() => toggleMultiSelect(s.id, opt, s.maxSelection)}
                >
                  {opt} {isActive && "✓"}
                </button>
              );
            })}
          </div>
        );

      case "bullets":
        const bullets = form[s.id] || ["", ""];
        return (
          <div className="bullets-container">
            {Array.from({ length: s.maxBullets }).map((_, i) => (
              <div key={i} className="bullet-row" style={{ alignItems: "flex-start" }}>
                <span className="bullet-marker" style={{ marginTop: "12px" }}>•</span>
                <div style={{ flex: 1, width: "100%" }}>
                  <input
                    type="text"
                    className="bullet-input"
                    placeholder={i === 0 ? s.placeholder : "Another belief..."}
                    maxLength={s.maxLength}
                    value={bullets[i] || ""}
                    onChange={(e) => updateBullet(s.id, i, e.target.value)}
                  />
                  <div className="character-count">
                    {(bullets[i] || "").length}/{s.maxLength} chars
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "composite":
        const subData = form[s.id] || {};
        return (
          <div className={`composite-container ${ s.id }`}>
            {s.fields.map(field => (
              <div key={field.id} className={`composite-field ${ field.id }`}>
                <label>{field.label}</label>
                {field.type === "select" ? (
                  <>
                    <select
                      className="custom-select small"
                      value={subData[field.id] || ""}
                      onChange={(e) => updateComposite(s.id, field.id, e.target.value)}
                    >
                      <option value="" disabled>Select...</option>
                      {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {/* Custom logic for timeHorizon */}
                    {s.id === 'outcome_group' && field.id === 'timeHorizon' && subData[field.id] === 'Custom' && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <input
                          type="text"
                          className="custom-input small"
                          placeholder="Enter custom timeline (e.g. 45 days)"
                          maxLength={50}
                          value={subData['timeHorizon_custom'] || ""}
                          onChange={(e) => updateComposite(s.id, 'timeHorizon_custom', e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                  </>
                ) : field.type === "textarea" ? (
                  /* ✅ NEW: Handle Textarea in Composite */
                  <div className="input-group">
                    <textarea
                      className="idea-input small"
                      placeholder={field.placeholder}
                      maxLength={field.maxLength}
                      value={subData[field.id] || ""}
                      onChange={(e) => updateComposite(s.id, field.id, e.target.value)}
                    />
                    <div className="character-count">
                      {(subData[field.id] || "").length}/{field.maxLength} chars
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    className="custom-input small"
                    placeholder={field.placeholder}
                    maxLength={field.maxLength || 200}
                    value={subData[field.id] || ""}
                    onChange={(e) => updateComposite(s.id, field.id, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="layout-container">
      <div className="focus-card">
        {/* ── HEADER & PROGRESS ── */}
        <header className="card-header">
          <div className="header-top">
            <span className="logo-text">MOONSHOT AI</span>
            <span className="step-counter">Step {step + 1} / {steps.length}</span>
          </div>

          <div className="progress-bar-container">
            <div
              className="progress-fill"
              style={{ width: `${ ((step + 1) / steps.length) * 100 }%` }}
            />
          </div>
        </header>

        {/* ── MAIN CONTENT ── */}
        <div className="card-content">
          <h1 className="question-title">{s.label}</h1>
          {s.helperText && <p className="question-helper">{s.helperText}</p>}

          <div className="input-area">
            {renderInput()}
          </div>
        </div>

        {/* ── FOOTER ACTIONS ── */}
        <footer className="card-footer">
          <button
            className="nav-btn prev"
            onClick={prevStep}
            style={{ opacity: 1, cursor: "pointer" }} // Ensure it looks active
          >
            ← Back
          </button>

          <button
            className="nav-btn next"
            disabled={!isValid() || isSubmitting}
            onClick={() => isLast ? createMinishot() : nextStep()}
          >
            {isSubmitting ? "Launching..." : isLast ? "Launch My Moonshot" : "Continue"}
          </button>
        </footer>
      </div>
    </div>
  );
}
